import React, { useState, useRef } from "react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";

interface AttachmentModalProps {
  onClose: () => void;
  onSend: (file: File) => Promise<void>;
}

const AttachmentModal: React.FC<AttachmentModalProps> = ({
  onClose,
  onSend,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleSend = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      await onSend(selectedFile);
      onClose();
    } catch (error) {
      console.error("Error sending attachment:", error);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileIcon = (type: string) => {
    if (type.includes("pdf")) return "📄";
    if (type.includes("word") || type.includes("document")) return "📝";
    if (type.includes("excel") || type.includes("spreadsheet")) return "📊";
    if (type.includes("powerpoint") || type.includes("presentation"))
      return "📽️";
    if (type.includes("video")) return "🎥";
    if (type.includes("audio")) return "🎵";
    return "📎";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-6 border w-full max-w-lg shadow-lg rounded-lg bg-base-100">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-base-content">
            Send Attachment
          </h3>
          <button
            onClick={onClose}
            className="text-base-content/50 hover:text-base-content/60"
            disabled={uploading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* File Selection */}
        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          />

          {!selectedFile ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-base-300 rounded-lg p-8 hover:border-base-300 transition-colors"
            >
              <DocumentIcon className="w-12 h-12 mx-auto text-base-content/50 mb-2" />
              <p className="text-base-content/80 text-sm">
                Click to select a file
              </p>
              <p className="text-base-content/50 text-xs mt-1">
                Images, PDFs, Documents, Spreadsheets
              </p>
            </button>
          ) : (
            <div className="border border-base-300 rounded-lg p-4">
              {/* Image Preview */}
              {previewUrl ? (
                <div className="mb-4">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-64 object-contain rounded-lg bg-base-200"
                  />
                </div>
              ) : (
                <div className="mb-4 flex items-center justify-center h-32 bg-base-200 rounded-lg">
                  <span className="text-6xl">
                    {getFileIcon(selectedFile.type)}
                  </span>
                </div>
              )}

              {/* File Info */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-base-content truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-base-content/60">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>

              {/* Change File Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700"
                disabled={uploading}
              >
                Change file
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-base-content/80 bg-base-200 rounded-md hover:bg-base-300 transition-colors"
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            className="px-4 py-2 bg-neutral text-white rounded-md hover:bg-neutral transition-colors disabled:opacity-50 flex items-center gap-2"
            disabled={!selectedFile || uploading}
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-base-100"></div>
                Uploading...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-5 h-5" />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachmentModal;
